import { ReactNode } from 'react';

import get from 'lodash/get';
import set from 'lodash/set';
import {
  AnySchema as YupSchema,
  ValidationError as YupValidationError,
} from 'yup';

type Translator = (errorObj: YupValidationError) => string | ReactNode;

export type ValidationError = {
  [key: string]: ValidationError | string;
};

function normalizeValidationError(
  err: YupValidationError,
  translator?: Translator
): ValidationError {
  return err.inner.reduce((errors, innerError) => {
    const { path, message } = innerError;
    const el: ReturnType<Translator> = translator
      ? translator(innerError)
      : message;

    // eslint-disable-next-line no-prototype-builtins
    if (path && errors.hasOwnProperty(path)) {
      const prev = get(errors, path);
      prev.push(el);
      set(errors, path, prev);
    } else if (path) {
      set(errors, path, [el]);
    }
    return errors;
  }, {});
}

/**
 * Wraps the execution of a Yup schema to return an Promise<ValidationError>
 * where the key is the form field and the value is the error string.
 */
export function makeValidate<T>(
  validator: YupSchema<T>,
  translator?: Translator
) {
  return async (values: T): Promise<ValidationError> => {
    try {
      await validator.validate(values, { abortEarly: false });
      return {};
    } catch (err) {
      return normalizeValidationError(err as YupValidationError, translator);
    }
  };
}

/**
 * Wraps the sync execution of a Yup schema to return an ValidationError
 * where the key is the form field and the value is the error string.
 */
export function makeValidateSync<T>(
  validator: YupSchema<T>,
  translator?: Translator
) {
  return (values: T): ValidationError => {
    try {
      validator.validateSync(values, { abortEarly: false });
      return {};
    } catch (err) {
      return normalizeValidationError(err as YupValidationError, translator);
    }
  };
}
